import React, { useContext, useMemo } from 'react';

import { FlowCardUtils } from '../../../store/flow-cards/FlowCardUtils';

import { useFlowCardTriggersById } from '../../../store/flow-cards/useFlowCardTriggers.ts';
import { useFlowCardConditionsById } from '../../../store/flow-cards/useFlowCardConditions.ts';
import { useFlowCardActionsById } from '../../../store/flow-cards/useFlowCardActions.ts';
import { useDevicesById } from '../../../store/devices/useDevices.ts';
import { useMoodsById } from '../../../store/moods/useMoods.ts';

const FlowCardExplorerContext = React.createContext({});

export function FlowCardExplorerContextProvider(props) {
  const flowCardTriggers = useFlowCardTriggersById();
  const flowCardConditions = useFlowCardConditionsById();
  const flowCardActions = useFlowCardActionsById();

  const devices = useDevicesById();
  const moods = useMoodsById();

  const context = useMemo(() => {
    if (
      devices.byId != null &&
      moods.byId != null &&
      flowCardTriggers.byId != null &&
      flowCardConditions.byId != null &&
      flowCardActions.byId != null
    ) {
      const triggers = processGroup({
        data: Object.entries(flowCardTriggers.byId),
        devicesById: devices.byId,
        moodsById: moods.byId,
      });

      const conditions = processGroup({
        data: Object.entries(flowCardConditions.byId),
        devicesById: devices.byId,
        moodsById: moods.byId,
      });

      const actions = processGroup({
        data: Object.entries(flowCardActions.byId),
        devicesById: devices.byId,
        moodsById: moods.byId,
      });

      return {
        triggers,
        conditions,
        actions,
      };
    }

    return {
      triggers: {
        byZoneId: {},
        byDeviceId: {},
        byAppUri: {},
        byManagerUri: {},
      },
      conditions: {
        byZoneId: {},
        byDeviceId: {},
        byAppUri: {},
        byManagerUri: {},
      },
      actions: {
        byZoneId: {},
        byDeviceId: {},
        byAppUri: {},
        byManagerUri: {},
      },
    };
  }, [
    devices.byId,
    moods.byId,
    flowCardTriggers.byId,
    flowCardConditions.byId,
    flowCardActions.byId,
  ]);

  return (
    <FlowCardExplorerContext.Provider value={context}>
      {props.children}
    </FlowCardExplorerContext.Provider>
  );
}

export function useFlowCardExplorerContext() {
  return useContext(FlowCardExplorerContext);
}

function processGroup({ data, devicesById, moodsById }) {
  return data.reduce(
    (accumulator, [key, flowCard]) => {
      const card = flowCard;
      let isGrouped = false;

      if (FlowCardUtils.isZoneCard(flowCard)) {
        isGrouped = true;
        const zoneId = flowCard.ownerUri.substring('homey:zone:'.length);

        accumulator.byZoneId[zoneId] = {
          ...accumulator.byZoneId[zoneId],
          cardsByKey: {
            ...(accumulator.byZoneId[zoneId]?.cardsByKey ?? null),
            [key]: card,
          },
        };
      }

      if (FlowCardUtils.isMoodCard(flowCard)) {
        isGrouped = true;
        const moodId = flowCard.ownerUri.substring('homey:mood:'.length);
        const mood = moodsById[moodId];

        if (mood != null) {
          accumulator.byZoneId[mood.zone] = {
            ...accumulator.byZoneId[mood.zone],
            moods: {
              ...(accumulator.byZoneId[mood.zone]?.moods ?? null),
              [mood.id]: { name: mood.name, card },
            },
          };
        }
      }

      if (FlowCardUtils.isDeviceCard(flowCard)) {
        isGrouped = true;
        const deviceId = flowCard.ownerUri.substring('homey:device:'.length);

        const device = devicesById[deviceId];

        if (device != null && device.flags?.includes('homey') === false) {
          const zoneId = device.zone;
          const driverId = device.driverId;
          const deviceName = device.name;

          accumulator.byZoneId[zoneId] = {
            ...accumulator.byZoneId[zoneId],
            devices: {
              ...(accumulator.byZoneId[zoneId]?.devices ?? null),
              [deviceId]: { name: deviceName },
            },
          };

          if (driverId.startsWith('homey:app')) {
            const appUri = driverId.split(':').slice(0, 3).join(':');

            accumulator.byAppUri[appUri] = {
              ...accumulator.byAppUri[appUri],
              devices: {
                ...(accumulator.byAppUri[appUri]?.devices ?? null),
                [deviceId]: { name: deviceName },
              },
            };
          } else if (driverId.startsWith('homey:manager')) {
            const managerUri = driverId.split(':').slice(0, 3).join(':');

            accumulator.byManagerUri[managerUri] = {
              ...accumulator.byManagerUri[managerUri],
              devices: {
                ...(accumulator.byManagerUri[managerUri]?.devices ?? null),
                [deviceId]: { name: deviceName },
              },
            };
          } else {
            if (driverId.startsWith('homey:virtualdriver') === false) {
              console.error('Failed to group by driver owner', flowCard);
            }
          }

          accumulator.byDeviceId[deviceId] = {
            ...accumulator.byDeviceId[deviceId],
            cardsByKey: {
              ...(accumulator.byDeviceId[deviceId]?.cardsByKey ?? null),
              [key]: card,
            },
          };
        }
      }

      if (FlowCardUtils.isAppCard(flowCard)) {
        isGrouped = true;
        const appUri = flowCard.ownerUri;

        accumulator.byAppUri[appUri] = {
          ...accumulator.byAppUri[appUri],
          cardsByKey: {
            ...(accumulator.byAppUri[appUri]?.cardsByKey ?? null),
            [key]: card,
          },
        };
      }

      if (FlowCardUtils.isManagerCard(flowCard)) {
        isGrouped = true;
        const managerUri = flowCard.ownerUri;

        accumulator.byManagerUri[managerUri] = {
          ...accumulator.byManagerUri[managerUri],
          cardsByKey: {
            ...(accumulator.byManagerUri[managerUri]?.cardsByKey ?? null),
            [key]: card,
          },
        };
      }

      if (isGrouped === false) {
        console.error('Failed to group card', flowCard);
      }

      return accumulator;
    },
    {
      byZoneId: {},
      byDeviceId: {},
      byAppUri: {},
      byManagerUri: {},
    }
  );
}
